/**
 * The six-essay AI Factory bundle. Three foundations converge on the Knowledge
 * Factory, which branches into the Ontology and Cognitive Factory essays.
 * Positions preserve the graph at every width; narrow containers use compact
 * cards, with horizontal scrolling below the minimum readable graph width.
 */

import type { AiFactorySeriesSlug } from "../ai-factory-motif/ai-factory-motif";

export type BundleNodeKind = "fundamental" | "hub" | "branch";

export interface BundleNode {
  slug: AiFactorySeriesSlug;
  title: string;
  kind: BundleNodeKind;
  order: string;
  summary: string;
  /** Top-left position and size in the 960x1120 desktop view box. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BundleEdge {
  from: AiFactorySeriesSlug;
  to: AiFactorySeriesSlug;
}

export const BUNDLE_VIEWBOX = { width: 960, height: 1120 } as const;

export const articleBundleNodes: BundleNode[] = [
  {
    slug: "vision-and-values",
    title: "Vision and Values",
    kind: "fundamental",
    order: "01",
    summary: "Human values decide which goals deserve pursuit.",
    x: 16,
    y: 20,
    width: 280,
    height: 300,
  },
  {
    slug: "understanding-and-bottlenecks",
    title: "Understanding and Bottlenecks",
    kind: "fundamental",
    order: "02",
    summary: "Complete learning loops move understanding into bounded teams.",
    x: 340,
    y: 20,
    width: 280,
    height: 300,
  },
  {
    slug: "truth-and-inference",
    title: "Truth and Inference",
    kind: "fundamental",
    order: "03",
    summary: "Philosophies of truth guide practical evaluation of LLM inference.",
    x: 664,
    y: 20,
    width: 280,
    height: 300,
  },
  {
    slug: "the-knowledge-factory",
    title: "The Knowledge Factory",
    kind: "hub",
    order: "04",
    summary: "Triggers, agent workflows, and feedback turn intent into verified outcomes.",
    x: 320,
    y: 408,
    width: 320,
    height: 300,
  },
  {
    slug: "the-ontology-factory",
    title: "The Ontology Factory",
    kind: "branch",
    order: "05",
    summary: "Repository structure makes ownership and dependencies visible.",
    x: 164,
    y: 800,
    width: 280,
    height: 300,
  },
  {
    slug: "the-cognitive-factory",
    title: "Cognitive Factory",
    kind: "branch",
    order: "06",
    summary: "Signals and discoverable memory expand what the factory can understand.",
    x: 516,
    y: 800,
    width: 280,
    height: 300,
  },
];

export const articleBundleEdges: BundleEdge[] = [
  { from: "vision-and-values", to: "the-knowledge-factory" },
  { from: "truth-and-inference", to: "the-knowledge-factory" },
  { from: "understanding-and-bottlenecks", to: "the-knowledge-factory" },
  { from: "the-knowledge-factory", to: "the-ontology-factory" },
  { from: "the-knowledge-factory", to: "the-cognitive-factory" },
];

export function bundleNodeCenter(node: BundleNode): { x: number; y: number } {
  return { x: node.x + node.width / 2, y: node.y + node.height / 2 };
}
